import PropTypes from 'prop-types';
import React from 'react';
import _isEmpty from 'lodash/isEmpty';
import _find from 'lodash/find';

import Typography from 'ravenjs/lib/Typography';
import { renderCheck } from 'ravenjs/utils/viewport';

import BreadcrumbSeparator from './BreadcrumbSeparator';
import BreadcrumbsItem from './BreadcrumbsItem';
import BreadcrumbsList from './BreadcrumbsList';
import BreadcrumbsStyled from './BreadcrumbsStyled';

// eslint-disable-next-line valid-jsdoc
/**
 * A Breadcrumbs component to help user navigate between sub-pages or a selection of items.
 *
 * @method      Breadcrumbs
 * @constructor
 */
function Breadcrumbs(props) {
    const { boxProps, itemComponent: Item, itemProps, items, separator, separatorProps } = props;
    const isMainBreadcrumb = _find(items, { id: 'home' });
    // Finalize the Separator component.
    const Separator =
        separator && typeof separator === 'function' ? separator : BreadcrumbSeparator;
    // Don't render any Breadcrumbs unless the items is available.
    if (_isEmpty(items)) {
        return null;
    }

    // Render the Breadcrumbs.
    return (
        <BreadcrumbsStyled as="nav" borderRadius="0" borderWidth="0" {...boxProps}>
            <BreadcrumbsList
                alignItems={isMainBreadcrumb ? 'center' : ''}
                display={isMainBreadcrumb ? (renderCheck('md', 'less') ? '' : 'flex') : ''}
            >
                {items.map((item, itemIdx) => {
                    const { href, label, onClick, ...rest } = item;
                    // NOTE: It's ok to use `index` to build this key,
                    // since these values won't be changing drastically during
                    // the render cycle.
                    const key = `${item.label}___${itemIdx}`;
                    if (itemProps) {
                        itemProps.type =
                            onClick && typeof onClick === 'function'
                                ? 'breadCrumbLink'
                                : 'breadCrumbItem';
                    }

                    // Render the item.
                    return (
                        <React.Fragment key={key}>
                            <BreadcrumbsItem>
                                <Item
                                    href={href}
                                    onClick={event => {
                                        event.preventDefault();
                                        if (typeof onClick === 'function') {
                                            onClick(event);
                                        }
                                    }}
                                    gutterBottom="0"
                                    title={label}
                                    {...itemProps}
                                    {...rest}
                                >
                                    {label}
                                </Item>
                            </BreadcrumbsItem>
                            {/* Insert the separator */}
                            {itemIdx < items.length - 1 && (
                                <BreadcrumbsItem>
                                    <Separator {...separatorProps}>{separator}</Separator>
                                </BreadcrumbsItem>
                            )}
                        </React.Fragment>
                    );
                })}
            </BreadcrumbsList>
        </BreadcrumbsStyled>
    );
}

Breadcrumbs.propTypes = {
    /**
     * Properties passed down to the top-level `Box` component.
     */
    boxProps: PropTypes.object,
    /**
     * The Component for each of the breadcrumb item(s).
     */
    itemComponent: PropTypes.elementType,
    /**
     * Properties passed down to each of the items.
     */
    itemProps: PropTypes.object,
    /**
     * A list of breadcrumb items to render.
     */
    items: PropTypes.array,
    /**
     * A Component or a String to render in place of the separator.
     */
    separator: PropTypes.node,
    /**
     * Properties passed down to the `Separator` component.
     */
    separatorProps: PropTypes.object,
};

Breadcrumbs.defaultProps = {
    boxProps: null,
    itemComponent: Typography,
    itemProps: null,
    items: null,
    separator: '/',
    separatorProps: null,
};

export default Breadcrumbs;
